import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, GripVertical, Trash2, ExternalLink, Pencil, ShoppingBag, Link2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import axios from 'axios';
import AddLinkDialog from './AddLinkDialog';
import ImageUpload from './ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinksEditor = ({ links, setLinks, profile, setProfile }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', url: '', link_type: 'url', icon: '', thumbnail_url: '', description: '', price: '' });

  const openEditDialog = (link) => {
    setEditingLink(link);
    setEditForm({
      title: link.title,
      url: link.url,
      link_type: link.link_type,
      icon: link.icon || '',
      thumbnail_url: link.thumbnail_url || '',
      description: link.description || '',
      price: link.price?.toString() || ''
    });
    setIsEditDialogOpen(true);
  };

  const handlePlatformAdd = async (platform, url) => {
    try {
      const response = await axios.post(`${API}/links`, {
        title: platform.name,
        url,
        link_type: 'social',
        icon: platform.icon,
      });
      setLinks([...links, response.data]);
      toast.success(`${platform.name} link added`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add link');
    }
  };

  const handleCustomAdd = async (formData) => {
    try {
      const response = await axios.post(`${API}/links`, formData);
      setLinks([...links, response.data]);
      toast.success('Link created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save link');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...editForm,
      price: editForm.link_type === 'product' && editForm.price ? parseFloat(editForm.price) : null
    };
    try {
      const response = await axios.put(`${API}/links/${editingLink.id}`, payload);
      setLinks(links.map(l => l.id === editingLink.id ? response.data : l));
      toast.success('Link updated');
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save link');
    }
  };

  const handleDelete = async (linkId) => {
    try {
      await axios.delete(`${API}/links/${linkId}`);
      setLinks(links.filter(l => l.id !== linkId));
      toast.success('Link deleted');
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleToggleActive = async (link) => {
    try {
      const response = await axios.put(`${API}/links/${link.id}`, { is_active: !link.is_active });
      setLinks(links.map(l => l.id === link.id ? response.data : l));
    } catch (error) {
      toast.error('Failed to update link');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(links);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setLinks(items);
    try {
      const response = await axios.post(`${API}/links/reorder`, { link_ids: items.map(item => item.id) });
      setLinks(response.data);
    } catch (error) {
      toast.error('Failed to reorder links');
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Your Links</h1>
          <p className="text-[#A1A1AA] mt-1">Add and manage your links</p>
        </div>
        <Button data-testid="add-link-btn" onClick={() => setIsAddDialogOpen(true)}
          className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add Link
        </Button>
      </div>

      {/* Links List */}
      <div className="flex items-center gap-2 mb-4 text-[#71717A] text-xs">
        <GripVertical className="w-3 h-3" />
        <span>Drag the handle to reorder links</span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="links">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {links.map((link, index) => (
                  <Draggable key={link.id} draggableId={link.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}
                        className={`rounded-2xl bg-[#121212] border border-[#27272A] p-4 transition-shadow ${snapshot.isDragging ? 'shadow-xl border-violet-500/50 z-50' : ''} ${!link.is_active ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-1 -ml-1 hover:bg-white/5 rounded-lg" data-testid={`drag-handle-${link.id}`}>
                            <GripVertical className="w-5 h-5 text-[#71717A]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {link.icon ? (
                                <span className="text-lg">{link.icon}</span>
                              ) : link.link_type === 'product' ? (
                                <ShoppingBag className="w-4 h-4 text-violet-400" />
                              ) : (
                                <Link2 className="w-4 h-4 text-[#71717A]" />
                              )}
                              <h3 className="font-medium text-white truncate">{link.title}</h3>
                              {link.link_type === 'product' && link.price && (
                                <span className="text-sm text-violet-400">${link.price}</span>
                              )}
                            </div>
                            <p className="text-sm text-[#71717A] truncate mt-1">{link.url}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#71717A]">{link.click_count} clicks</span>
                            <button data-testid={`toggle-link-${link.id}`} onClick={() => handleToggleActive(link)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                              {link.is_active ? <ToggleRight className="w-5 h-5 text-violet-400" /> : <ToggleLeft className="w-5 h-5 text-[#71717A]" />}
                            </button>
                            <button data-testid={`edit-link-${link.id}`} onClick={() => openEditDialog(link)} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                              <Pencil className="w-4 h-4 text-[#A1A1AA]" />
                            </button>
                            <button data-testid={`delete-link-${link.id}`} onClick={() => handleDelete(link.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {links.length === 0 && (
        <div className="text-center py-16">
          <Link2 className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">No links yet</h3>
          <p className="text-[#A1A1AA] mb-6">Add your first link to get started</p>
          <Button data-testid="add-first-link-btn" onClick={() => setIsAddDialogOpen(true)}
            className="rounded-full bg-violet-500 hover:bg-violet-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add your first link
          </Button>
        </div>
      )}

      {/* Unified Add Link Dialog */}
      <AddLinkDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onPlatformSelect={handlePlatformAdd}
        onCustomLinkSubmit={handleCustomAdd}
        existingPlatforms={links.filter(l => l.link_type === 'social').map(l => l.title?.toLowerCase())}
        mode="link"
      />

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Edit Link</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Update your link details below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label className="text-[#EDEDED]">Link Type</Label>
              <Select value={editForm.link_type} onValueChange={(v) => setEditForm({ ...editForm, link_type: v })}>
                <SelectTrigger data-testid="link-type-select" className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-[#27272A] z-[200]" position="popper" sideOffset={4}>
                  <SelectItem value="url">Regular Link</SelectItem>
                  <SelectItem value="menu">Menu (PDF / Link)</SelectItem>
                  <SelectItem value="product">Product / Shop</SelectItem>
                  <SelectItem value="social">Social Media</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#EDEDED]">Title</Label>
              <Input data-testid="link-title-input" value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="My Website" required />
            </div>
            <div>
              <Label className="text-[#EDEDED]">URL</Label>
              <Input data-testid="link-url-input" value={editForm.url}
                onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://example.com" type="url" required />
            </div>
            {editForm.link_type === 'product' && (
              <div>
                <Label className="text-[#EDEDED]">Price ($)</Label>
                <Input data-testid="link-price-input" value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="29.99" type="number" step="0.01" />
              </div>
            )}
            <div>
              <Label className="text-[#EDEDED]">Icon (emoji, optional)</Label>
              <Input data-testid="link-icon-input" value={editForm.icon}
                onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="🔗" maxLength={2} />
            </div>
            <div>
              <Label className="text-[#EDEDED]">Thumbnail Image</Label>
              <div className="mt-2">
                <ImageUpload
                  value={editForm.thumbnail_url}
                  onChange={(url) => setEditForm({ ...editForm, thumbnail_url: url })}
                  label="Edit Thumbnail"
                  shape="rect"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#EDEDED]">Description (optional)</Label>
              <Textarea data-testid="link-description-input" value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="Brief description..." rows={2} />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}
                className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Cancel</Button>
              <Button data-testid="save-link-btn" type="submit"
                className="bg-violet-500 hover:bg-violet-600 text-white">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LinksEditor;
